import React, { useCallback, useEffect, useState } from 'react';
import { Card, Tabs, Table, Tag, Typography } from 'antd';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Text } = Typography;
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

// Traduz status técnico da compra pra copy pt-BR amigável.
const PURCHASE_STATUS = {
  paid: { color: 'green', label: 'Pago' },
  pending: { color: 'orange', label: 'Aguardando pagamento' },
  failed: { color: 'red', label: 'Falhou' },
  expired: { color: 'default', label: 'Expirou' },
  cancelled: { color: 'default', label: 'Cancelado' },
  granted: { color: 'purple', label: 'Cortesia' },
};

// Traduz motivo do débito de crédito. Cobre todos os `reason` que o
// backend grava em community_credit_debits.
const DEBIT_REASON = {
  charge_created: 'Cobrança criada',
  charge_added: 'Destinatário adicionado',
  whatsapp_sent: 'Envio WhatsApp',
  whatsapp_reminder: 'Reforço WhatsApp',
  batch_charges_failed: 'Estorno · envio falhou',
  batch_failed_fix: 'Ajuste manual',
  cost_recalc_fix: 'Ajuste manual',
  manual_adjustment: 'Ajuste manual',
};

const PAYMENT_METHOD = {
  pix: 'Pix',
  card: 'Cartão',
  credit_card: 'Cartão',
};

// Historico de compras + gastos de crédito. props: { communityTeamId }
export default function CreditsHistoryTable({ communityTeamId }) {
  const [purchases, setPurchases] = useState([]);
  const [debits, setDebits] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!communityTeamId) return;
    setLoading(true);
    try {
      const r = await api.get('/community/finance/credits/status', { params: { team_id: communityTeamId } });
      setPurchases(r.data.purchases || []);
      setDebits(r.data.debits || []);
    } finally { setLoading(false); }
  }, [communityTeamId]);
  useEffect(() => { load(); }, [load]);

  const purchaseCols = [
    { title: 'Data', dataIndex: 'created_at', width: 110,
      render: v => v ? <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm')}</span> : '—' },
    { title: 'Créditos', dataIndex: 'pack_size', width: 80, align: 'right' },
    { title: 'Valor', dataIndex: 'total_price_brl', width: 90, align: 'right',
      render: v => <span style={{ whiteSpace: 'nowrap' }}>{fmtBRL(v)}</span> },
    { title: 'Como pagou', dataIndex: 'payment_method', width: 100,
      render: v => v ? <Tag color={v === 'pix' ? 'green' : 'blue'}>{PAYMENT_METHOD[v] || v}</Tag> : <Text type="secondary">—</Text> },
    { title: 'Situação', dataIndex: 'status', width: 130, render: v => {
      const s = PURCHASE_STATUS[v] || { color: 'default', label: v };
      return <Tag color={s.color}>{s.label}</Tag>;
    } },
    { title: 'MP', dataIndex: 'mp_payment_id', width: 100,
      render: v => v ? <Text style={{ fontSize: 10 }}>#{v}</Text> : <Text type="secondary">—</Text> },
  ];
  const debitCols = [
    { title: 'Data', dataIndex: 'created_at', width: 110,
      render: v => v ? <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm')}</span> : '—' },
    { title: 'Créditos', dataIndex: 'amount', width: 80, align: 'right',
      render: v => {
        const n = Number(v);
        // amount negativo = refund/crédito de volta pro saldo
        return (
          <Text strong style={{ color: n < 0 ? '#128C7E' : '#dc2626', whiteSpace: 'nowrap' }}>
            {n < 0 ? '+' : '−'} {Math.abs(n)}
          </Text>
        );
      } },
    { title: 'Motivo', dataIndex: 'reason',
      render: v => DEBIT_REASON[v] || v },
  ];

  return (
    <Card styles={{ body: { padding: 12 } }}>
      <Tabs
        items={[
          { key: 'purchases', label: `Compras (${purchases.length})`, children:
            <div style={{ overflowX: 'auto' }}>
              <Table rowKey="id" size="small" loading={loading}
                columns={purchaseCols} dataSource={purchases}
                scroll={{ x: 600 }}
                pagination={{ pageSize: 10, simple: true, size: 'small' }} />
            </div> },
          { key: 'debits', label: `Uso (${debits.length})`, children:
            <div style={{ overflowX: 'auto' }}>
              <Table rowKey="id" size="small" loading={loading}
                columns={debitCols} dataSource={debits}
                scroll={{ x: 400 }}
                pagination={{ pageSize: 10, simple: true, size: 'small' }} />
            </div> },
        ]}
      />
    </Card>
  );
}
